/* Pure functions to help with e.g. data conversion */
import { Feature, LineString, Point, Polygon as GeoJSONPolygon } from "geojson";
import L from "leaflet";
import { latLng, latLngBounds, LatLngBounds, LatLngLiteral } from "leaflet";
import { difference } from "lodash";
import {
  HasId,
  Indication,
  MultilineCell,
  Polygon,
  UpdateValueWithMaybeReasonForChange,
  UUID,
  ZoomDefinition,
  MultilineCategory,
  MultilineAnnotation,
  ImageWithAnnotations,
  Fragment,
  HpfAnno,
  Annotation,
  HPFAnnotation,
  PointAnno,
  PointAnnotation,
  FreehandAnnotation,
  AnnotationClassType,
  AnnotationClassWithCount
} from "./models";

import { isThisYear, isToday, parse } from "date-fns";
import format from "date-fns/format";
import { extname } from "path";
import { AnnotationType } from "./slices/caseImageViewer";
import { AnnotationClassForm } from "./slices/studyConfiguration";
import { RootState } from "./store";

export const LOCALE = "en-CA";

export function pointToGeoJSON(latLng: LatLngLiteral): Feature<Point> {
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [latLng.lat, latLng.lng]
    },
    properties: {}
  };
}

export function lineToGeoJSON(latLngs: ReadonlyArray<LatLngLiteral>): Feature<LineString> {
  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: latLngs.map(l => [l.lat, l.lng])
    },
    properties: {}
  };
}

export function polygonToGeoJSON(latLngs: ReadonlyArray<LatLngLiteral>): Feature<GeoJSONPolygon> {
  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [latLngs.map(l => [l.lat, l.lng])]
    },
    properties: {}
  };
}

export function toLatLngBounds(poly: Polygon): LatLngBounds | undefined {
  const coords = poly.coordinates[0]?.map(arr => latLng(arr[1], arr[0]));
  return coords && latLngBounds(coords);
}

export function getMaxZoom(zooms: ReadonlyArray<ZoomDefinition>): number {
  return zooms.map(obj => obj.zoom).reduce((x, y) => Math.max(x, y), 0);
}

export function getMinZoom(zooms: ReadonlyArray<ZoomDefinition>): number {
  return zooms.map(obj => obj.zoom).reduce((x, y) => Math.min(x, y), 30);
}

export function parseLISDateString(dateString: string | null): Date | null {
  if (dateString === null) {
    return null;
  }
  const date = parse(dateString, "yyyyMMddHHmmss", new Date());
  return isNaN(date.getTime()) ? null : date;
}

export function formatDate(date: Date | null): string {
  return date instanceof Date
    ? isToday(date)
      ? format(date, "h:mma")
      : isThisYear(date)
      ? format(date, "MMM d")
      : format(date, "MMM d yyyy")
    : "—";
}

export function formateDateToYearMonthDay(date: Date | null): string {
  return date instanceof Date
    ? date.toLocaleDateString(LOCALE, { month: "long", day: "numeric", year: "numeric" })
    : "-";
}

export function formateDateToYearMonthDayTime(date: Date | null): string {
  return date instanceof Date
    ? date.toLocaleDateString(LOCALE, {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZoneName: "short"
      })
    : "-";
}

export function getDaysFromDateSelected(date: Date): number {
  const today = new Date();
  const differenceMs = date.getTime() - today.getTime();
  const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

  return differenceDays;
}

function distanceToMeters(distance: number, pixelSize: number, resolutionInMeters: number): number {
  return (distance * resolutionInMeters) / pixelSize;
}

export function metersToMicrometers(meters: number): number {
  return meters * 10 ** 6;
}

export function toMicrometers(
  distance: number,
  pixelSize: number,
  resolutionInMeters: number
): number {
  const meters = distanceToMeters(distance, pixelSize, resolutionInMeters);
  return metersToMicrometers(meters);
}

export function formatMicrometers(micrometers: number): string {
  return `${Math.floor(micrometers)} μm`;
}

/**
 * Calculate the number of micrometers (μm) corresponding to a given distance on current map.
 */
export function mapDistanceToMicrometers(
  map: L.Map,
  distance: number,
  pixelSize: number,
  resolutionInMeters: number
): number {
  // NOTE: Borrowed logic from built in Leaflet scale control
  // See: https://github.com/Leaflet/Leaflet/blob/37d2fd15ad6518c254fae3e033177e96c48b5012/src/control/Control.Scale.js#L68-L73
  const y = map.getSize().y / 2;
  const width = map.distance(
    map.containerPointToLatLng([0, y]),
    map.containerPointToLatLng([distance, y])
  );
  return metersToMicrometers(distanceToMeters(width, pixelSize, resolutionInMeters));
}

export function getHpfRadiusInMeters(
  pixelSize: number,
  resolutionInMeters: number,
  indications: ReadonlyArray<Indication> | null
): number {
  // Most studies use a standard zoom of 40x which is 0.238mm² known as 1 HPF
  // NASH studies use a 20x zoom
  const hpfFactor =
    indications && indications.includes(Indication.NonalcoholicSteatohepatitis) ? 2 : 1;
  const hpfAreaInSquareMeters = 0.238 * 10 ** -6;
  const hpfRadiusInMeters = Math.sqrt(hpfAreaInSquareMeters / Math.PI); // radius = sqrt(Area / PI)
  return (hpfFactor * (hpfRadiusInMeters * pixelSize)) / resolutionInMeters;
}

/*
 * Test whether sets of ids are different to detect changes in form input.
 */
export function areDifferent<T extends string | number>(
  a: ReadonlyArray<T>,
  b: ReadonlyArray<T>
): boolean {
  return a.length !== b.length
    ? true
    : difference(Array.from(a).sort(), Array.from(b).sort()).length > 0;
}

/*
 * Creates a name for a duplicate image
 */
export function duplicateImageName(imageName: string, suffix: string = "_d"): string {
  return imageName.replace(extname(imageName), `${suffix}${extname(imageName)}`);
}

/*
 * Creates an accession number for a duplicate image
 */
export function duplicateAccessionNumber(
  accessionNumber: string | null,
  suffix: string = "_d"
): string | null {
  return accessionNumber ? `${accessionNumber}${suffix}` : null;
}

export function idsOnly(haveId: readonly HasId[]): UUID[] {
  return haveId.map(({ id }) => id);
}

export function updateWithIdsOnly({
  value,
  reasonForChange
}: UpdateValueWithMaybeReasonForChange<readonly HasId[]>): UpdateValueWithMaybeReasonForChange<
  UUID[]
> {
  return {
    value: idsOnly(value),
    ...(reasonForChange ? { reasonForChange } : {})
  };
}

export function getNextUnusedMultilineCell(
  imageWithAnnotations: ImageWithAnnotations,
  exclude?: MultilineCell,
  start?: MultilineCell
): MultilineCell | null {
  const annotations = imageWithAnnotations.annotations;

  const multilineAnnotations = annotations.filter(
    annotation => annotation.annotationType == AnnotationType.Multiline
  ) as MultilineAnnotation[];

  const multilineAnnotationClassesWithCount = imageWithAnnotations.multilineAnnotationClassesWithCount.filter(
    awc => awc.annotationClass.enabled
  );

  if (multilineAnnotationClassesWithCount.length === 0) {
    return null;
  }

  const categories = [MultilineCategory.VillusHeight, MultilineCategory.CryptDepth];

  const hasStart = start && start.class !== null && start.category !== null;

  const startIndexClass = hasStart
    ? multilineAnnotationClassesWithCount.findIndex(
        annotationClass => annotationClass.annotationClass.id === start.class.id
      )
    : 0;

  const startIndexCategory = hasStart
    ? categories.findIndex(category => category === start.category)
    : 0;

  const result = multilineAnnotationClassesWithCount
    .slice(startIndexClass)
    .flatMap(multilineAnnotationClass =>
      categories
        .slice(
          multilineAnnotationClass === multilineAnnotationClassesWithCount[startIndexClass]
            ? startIndexCategory
            : 0
        )
        .map(multilineCategory => ({
          class: multilineAnnotationClass.annotationClass,
          category: multilineCategory
        }))
    )
    .find(
      cell =>
        !(exclude && exclude.class === cell.class && exclude.category === cell.category) &&
        !multilineAnnotations.some(
          annotation =>
            annotation.annotationClassId === cell.class.id &&
            annotation.multilineCategory === cell.category
        )
    );

  return result || null;
}

const ORPHAN = "Orphan";

export function isPointInPolygon(point: LatLngLiteral, polygon: Polygon): boolean {
  const coords = polygon.coordinates[0];
  if (!coords || coords.length === 0) return false;
  return coords.reduce<boolean>((inside, coordI, i) => {
    const coordJ = coords[i === 0 ? coords.length - 1 : i - 1];
    if (!coordI || !coordJ) return inside;
    const xi = coordI[0],
      yi = coordI[1];
    const xj = coordJ[0],
      yj = coordJ[1];
    const intersect =
      yi > point.lng !== yj > point.lng &&
      point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi;
    return intersect ? !inside : inside;
  }, false);
}

function getBoundingBox(
  polygon: Polygon
): { minLat: number; maxLat: number; minLng: number; maxLng: number } | null {
  const coords = polygon.coordinates[0];
  if (!coords || coords.length === 0) return null;
  return coords.reduce(
    (bbox, coord) => {
      if (!coord) return bbox;
      return {
        minLat: Math.min(bbox.minLat, coord[0]),
        maxLat: Math.max(bbox.maxLat, coord[0]),
        minLng: Math.min(bbox.minLng, coord[1]),
        maxLng: Math.max(bbox.maxLng, coord[1])
      };
    },
    { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity }
  );
}

function isPointInBoundingBox(
  point: LatLngLiteral,
  bbox: { minLat: number; maxLat: number; minLng: number; maxLng: number }
): boolean {
  return (
    point.lat >= bbox.minLat &&
    point.lat <= bbox.maxLat &&
    point.lng >= bbox.minLng &&
    point.lng <= bbox.maxLng
  );
}

function getOrphanPoints(
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const myPoints = annotations.filter(
    a => a.annotationType === "POINT" && (a as PointAnnotation).parent === null
  );
  const myPointsNoClass = annotations.filter(
    a =>
      a.annotationType === "POINT" &&
      (a as PointAnnotation).parent === null &&
      !(a as PointAnnotation).annotationClassId
  );

  // Separate manual and AI points
  const manualPoints = myPoints.filter(p => !p.isAi);
  const aiPoints = myPoints.filter(p => p.isAi);
  const manualPointsNoClass = myPointsNoClass.filter(p => !p.isAi);
  const aiPointsNoClass = myPointsNoClass.filter(p => p.isAi);

  const manualOpts = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => (p as PointAnnotation).annotationClassId == cls.id);
      return {
        id: cls.id || "no-id",
        name: cls.name,
        color: cls.color,
        count: pts.length,
        pointIds: pts.map(a => a.id)
      };
    })
    .filter(panno => panno.count > 0);

  const aiOpts = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => (p as PointAnnotation).annotationClassId == cls.id);
      return {
        id: (cls.id || "no-id") + "-ai",
        name: cls.name + "-AI",
        color: cls.color,
        count: pts.length,
        pointIds: pts.map(a => a.id)
      };
    })
    .filter(panno => panno.count > 0);

  const defaultPoints = [
    ...(manualPointsNoClass.length > 0
      ? [
          {
            id: "no-p-cl-id",
            name: "Default",
            color: "#00FF00",
            count: manualPointsNoClass.length,
            pointIds: manualPointsNoClass.map(a => a.id)
          }
        ]
      : []),
    ...(aiPointsNoClass.length > 0
      ? [
          {
            id: "no-p-cl-id-ai",
            name: "Default-AI",
            color: "#00FF00",
            count: aiPointsNoClass.length,
            pointIds: aiPointsNoClass.map(a => a.id)
          }
        ]
      : [])
  ];

  return [...defaultPoints, ...manualOpts, ...aiOpts];
}

function mapStateToUnifiedFragsInternal(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  fhcls: readonly AnnotationClassForm<"FREEHAND">[]
): ReadonlyArray<Fragment> {
  const manualAnnotations = annotations.filter(a => !a.isAi);
  const aiAnnotations = annotations.filter(a => a.isAi);

  const manualFragments = mapStateToFragsOriginal(manualAnnotations, fcls, pcls, annotations);
  const aiFragments = mapStateToFragsOriginal(aiAnnotations, fcls, pcls, annotations).map(
    fragment => ({
      ...fragment,
      name: fragment.name === "HPF + Points" ? "HPF + Points" : fragment.name + "-AI",
      isAI: true,
      hpfs:
        fragment.hpfs?.map(hpf => ({
          ...hpf,
          isAI: true,
          name: hpf.name === ORPHAN ? ORPHAN : hpf.name
        })) || []
    })
  );

  const manualFreehandFragments = buildFreehandFragments(
    manualAnnotations,
    fhcls,
    pcls,
    annotations
  );
  const aiFreehandFragments = buildFreehandFragments(aiAnnotations, fhcls, pcls, annotations).map(
    fragment => ({
      ...fragment,
      name: fragment.name === "Freehand + Points" ? "Freehand + Points" : fragment.name + "-AI",
      isAI: true,
      hpfs:
        fragment.hpfs?.map(fh => ({
          ...fh,
          isAI: true
        })) || []
    })
  );

  // Merge "HPF + Points" fragments
  const otherManual = manualFragments.find(f => f.name === "HPF + Points");
  const otherAI = aiFragments.find(f => f.name === "HPF + Points");

  const mergedHpfFragments = (() => {
    if (otherManual && otherAI) {
      const manualOrphan = otherManual.hpfs?.find(h => h.name === ORPHAN);
      const aiOrphan = otherAI.hpfs?.find(h => h.name === ORPHAN);
      const manualNonOrphan = otherManual.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const aiNonOrphan = otherAI.hpfs?.filter(h => h.name !== ORPHAN) || [];

      const mergedOrphan =
        manualOrphan && aiOrphan
          ? [
              {
                ...manualOrphan,
                points: [...(manualOrphan.points || []), ...(aiOrphan.points || [])],
                count: (manualOrphan.count || 0) + (aiOrphan.count || 0)
              }
            ]
          : manualOrphan
          ? [manualOrphan]
          : aiOrphan
          ? [aiOrphan]
          : [];

      const mergedHpfs = [...mergedOrphan, ...manualNonOrphan, ...aiNonOrphan];

      return [
        {
          ...otherManual,
          hpfs: mergedHpfs
        }
      ];
    } else if (otherManual) {
      // Ensure Orphan is first even when only manual exists
      const orphan = otherManual.hpfs?.find(h => h.name === ORPHAN);
      const nonOrphan = otherManual.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const sortedHpfs = orphan ? [orphan, ...nonOrphan] : nonOrphan;

      return [
        {
          ...otherManual,
          hpfs: sortedHpfs
        }
      ];
    } else if (otherAI) {
      // Ensure Orphan is first even when only AI exists
      const orphan = otherAI.hpfs?.find(h => h.name === ORPHAN);
      const nonOrphan = otherAI.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const sortedHpfs = orphan ? [orphan, ...nonOrphan] : nonOrphan;

      return [
        {
          ...otherAI,
          hpfs: sortedHpfs
        }
      ];
    }
    return [];
  })();

  // Merge "Freehand + Points" fragments
  const freehandManual = manualFreehandFragments.find(f => f.name === "Freehand + Points");
  const freehandAI = aiFreehandFragments.find(f => f.name === "Freehand + Points");

  const mergedFreehandFragments = (() => {
    if (freehandManual && freehandAI) {
      return [
        {
          ...freehandManual,
          hpfs: [...(freehandManual.hpfs || []), ...(freehandAI.hpfs || [])]
        }
      ];
    } else if (freehandManual) {
      return [freehandManual];
    } else if (freehandAI) {
      return [freehandAI];
    }
    return [];
  })();

  return [
    ...mergedHpfFragments,
    ...mergedFreehandFragments,
    ...manualFragments.filter(f => f.name !== "HPF + Points"),
    ...aiFragments.filter(f => f.name !== "HPF + Points"),
    ...manualFreehandFragments.filter(f => f.name !== "Freehand + Points"),
    ...aiFreehandFragments.filter(f => f.name !== "Freehand + Points")
  ];
}

function mapStateToFragsOriginal(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  allAnnotationsForPoints: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  const orphanPoints = getOrphanPoints(pcls, annotations);

  // Create fragments from HPF classes
  const classifiedFragments = fcls
    .map(fc => {
      const hpfs = annotations
        .filter(a => a.annotationType === "HPF" && (a as HPFAnnotation).annotationClassId === fc.id)
        .map(hpf => {
          const hpfAnnotation = hpf as HPFAnnotation;
          const pts = getPointsForHpf(hpfAnnotation, pcls, allAnnotationsForPoints);
          return {
            id: hpf.id || "no-id",
            name: "",
            count: pts.reduce((s, c) => s + c.count, 0),
            color: hpfAnnotation.color || fc.color,
            isOpen: false,
            isAI: false,
            points: pts
          };
        });
      return {
        id: fc.id,
        name: fc.name,
        count: hpfs.reduce((s, c) => s + c.count, 0),
        color: fc.color,
        isOpen: true,
        isAI: false,
        hpfs
      };
    })
    .filter(f => f.hpfs.length > 0);

  const defaultHpfs = annotations
    .filter(a => a.annotationType === "HPF" && !(a as HPFAnnotation).annotationClassId)
    .map(hpf => {
      const hpfAnnotation = hpf as HPFAnnotation;
      const pts = getPointsForHpf(hpfAnnotation, pcls, allAnnotationsForPoints);
      return {
        id: hpf.id || "no-id",
        name: "",
        count: pts.reduce((s, c) => s + c.count, 0),
        color: hpfAnnotation.color || "#00FF00",
        isOpen: false,
        isAI: false,
        points: pts
      };
    });

  // Build "Other" fragment HPFs
  const orphanHpf = orphanPoints.length > 0 ? [createOrphanHpf(orphanPoints)] : [];
  const otherHpfs = [...orphanHpf, ...defaultHpfs];

  // Return fragments with "Other" if needed
  if (otherHpfs.length === 0) {
    return classifiedFragments;
  }

  return [createOtherFragment(otherHpfs), ...classifiedFragments];
}

function getPointsForHpf(
  hpf: HPFAnnotation,
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const myPoints = annotations.filter(
    a => a.annotationType === "POINT" && (a as PointAnnotation).parent === hpf.id
  );

  const manualPoints = myPoints.filter(p => !p.isAi);
  const aiPoints = myPoints.filter(p => p.isAi);

  const classifiedManualPoints = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => (p as PointAnnotation).annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name,
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const classifiedAiPoints = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => (p as PointAnnotation).annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name + "-AI",
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const defaultManualPoints = manualPoints.filter(p => !(p as PointAnnotation).annotationClassId);
  const defaultManualPointAnno =
    defaultManualPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default",
            color: "#FFFF00",
            count: defaultManualPoints.length,
            pointIds: defaultManualPoints.map(a => a.id)
          }
        ]
      : [];

  const defaultAiPoints = aiPoints.filter(p => !(p as PointAnnotation).annotationClassId);
  const defaultAiPointAnno =
    defaultAiPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default-AI",
            color: "#FFFF00",
            count: defaultAiPoints.length,
            pointIds: defaultAiPoints.map(a => a.id)
          }
        ]
      : [];

  return [
    ...defaultManualPointAnno,
    ...classifiedManualPoints,
    ...defaultAiPointAnno,
    ...classifiedAiPoints
  ];
}

function createOrphanHpf(pts: ReadonlyArray<PointAnno>): HpfAnno {
  return {
    id: "no-id2",
    name: ORPHAN,
    count: pts.reduce((s, c) => s + c.count, 0),
    color: "black",
    isOpen: false,
    isAI: false,
    points: pts
  };
}

function createOtherFragment(hpfs: ReadonlyArray<HpfAnno>): Fragment {
  return {
    id: "no-id",
    name: "HPF + Points",
    count: 0,
    color: "black",
    isOpen: true,
    isAI: false,
    hpfs
  };
}

function buildFreehandFragments(
  annotations: ReadonlyArray<Annotation>,
  fhcls: readonly AnnotationClassForm<"FREEHAND">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  allAnnotations: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  const freehandAnnotations = annotations.filter(
    a => a.annotationType === "FREEHAND"
  ) as ReadonlyArray<FreehandAnnotation>;

  if (freehandAnnotations.length === 0) return [];

  const classifiedFragments = fhcls
    .map(fc => {
      const freehands = freehandAnnotations
        .filter(fh => fh.annotationClassId === fc.id)
        .map(fh => {
          const pts = getPointsForFreehand(fh, pcls, allAnnotations);
          return {
            id: fh.id || "no-id",
            name: "",
            count: pts.reduce((s, c) => s + c.count, 0),
            color: fh.color || fc.color,
            isOpen: false,
            isAI: false,
            points: pts
          };
        });
      return {
        id: fc.id,
        name: fc.name,
        count: freehands.reduce((s, c) => s + c.count, 0),
        color: fc.color,
        isOpen: true,
        isAI: false,
        hpfs: freehands
      };
    })
    .filter(f => f.hpfs.length > 0);

  const defaultFreehands = freehandAnnotations
    .filter(fh => !fh.annotationClassId)
    .map(fh => {
      const pts = getPointsForFreehand(fh, pcls, allAnnotations);
      return {
        id: fh.id || "no-id",
        name: "",
        count: pts.reduce((s, c) => s + c.count, 0),
        color: fh.color || "#FFFF00",
        isOpen: false,
        isAI: false,
        points: pts
      };
    });

  if (defaultFreehands.length === 0 && classifiedFragments.length === 0) {
    return [];
  }

  const otherFreehands =
    defaultFreehands.length > 0
      ? [
          {
            id: "no-id",
            name: "Freehand + Points",
            count: 0,
            color: "black",
            isOpen: true,
            isAI: false,
            hpfs: defaultFreehands
          }
        ]
      : [];

  return [...otherFreehands, ...classifiedFragments];
}

function getPointsForFreehand(
  freehand: FreehandAnnotation,
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const allPoints = annotations.filter(
    a => a.annotationType === "POINT"
  ) as ReadonlyArray<PointAnnotation>;

  const bbox = getBoundingBox(freehand.geometry);
  const pointsInside = bbox
    ? allPoints.filter(p => {
        const coords = p.geometry.coordinates;
        const point = { lat: coords[0], lng: coords[1] };
        return isPointInBoundingBox(point, bbox) && isPointInPolygon(point, freehand.geometry);
      })
    : [];

  const manualPoints = pointsInside.filter(p => !p.isAi);
  const aiPoints = pointsInside.filter(p => p.isAi);

  const classifiedManualPoints = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => p.annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name,
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const classifiedAiPoints = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => p.annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name + "-AI",
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const defaultManualPoints = manualPoints.filter(p => !p.annotationClassId);
  const defaultManualPointAnno =
    defaultManualPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default",
            color: "#FFFF00",
            count: defaultManualPoints.length,
            pointIds: defaultManualPoints.map(a => a.id)
          }
        ]
      : [];

  const defaultAiPoints = aiPoints.filter(p => !p.annotationClassId);
  const defaultAiPointAnno =
    defaultAiPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default-AI",
            color: "#FFFF00",
            count: defaultAiPoints.length,
            pointIds: defaultAiPoints.map(a => a.id)
          }
        ]
      : [];

  return [
    ...defaultManualPointAnno,
    ...classifiedManualPoints,
    ...defaultAiPointAnno,
    ...classifiedAiPoints
  ];
}

export function mapStateToFrags(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  _allAnnotationsForPoints?: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  return mapStateToFragsOriginal(annotations, fcls, pcls, annotations);
}

export function mapStateToUnifiedFrags(state: RootState): ReadonlyArray<Fragment> {
  if (
    !state.caseImageViewer.imageWithAnnotations ||
    !("resource" in state.caseImageViewer.imageWithAnnotations)
  ) {
    return [];
  }

  const imageResource = state.caseImageViewer.imageWithAnnotations.resource;
  const { annotations } = imageResource;

  // Derive annotation classes from the image response itself, which always contains the
  // annotation classes for the study that the image belongs to.  This avoids a dependency on
  // studyConfiguration state (populated via a separate studyFetchRequest call) which may not
  // yet be loaded — or may never be populated for non-admin users whose study response does not
  // include the full admin view.
  const toClassForm = <T extends AnnotationClassType>(
    ac: AnnotationClassWithCount<T>
  ): AnnotationClassForm<T> => ({
    ...ac.annotationClass,
    count: ac.count,
    deleted: false
  });

  const fragClasses = imageResource.hpfAnnotationClassesWithCount.map(toClassForm);
  const pointClasses = imageResource.pointAnnotationClassesWithCount.map(toClassForm);
  const freehandClasses = imageResource.freehandAnnotationClassesWithCount.map(toClassForm);

  return mapStateToUnifiedFragsInternal(annotations, fragClasses, pointClasses, freehandClasses);
}

export function mapStateToFragments(state: RootState): ReadonlyArray<Fragment> {
  return mapStateToUnifiedFrags(state);
}

export function convertCoordinatesToLatLngLiteral(coordinates: number[]): LatLngLiteral {
  return {
    lat: coordinates[0] || 0,
    lng: coordinates[1] || 0
  };
}
